import type { ApiEndpoints } from '@avisoma/shared';

export type ClusterResponse = ApiEndpoints<string>['/api/category-clusters/:id']['GET']['response'];
export type ClusterPage = ClusterResponse['pages'][number];

/** Fraction of members whose top classification matches the cluster's category. */
export function agreement(pages: ClusterPage[], categoryName: string | undefined) {
  const total = pages.length;
  const agree = pages.filter((p) => p.classificationCategory === categoryName).length;
  return { agree, total, rate: total === 0 ? 0 : agree / total };
}

/** Members whose top classification is a different category — natural relabel targets. */
export function mislabelCandidates(pages: ClusterPage[], categoryName: string | undefined): ClusterPage[] {
  return pages.filter((p) => p.classificationCategory !== categoryName);
}

/** Margin between the top two classification scores for a member (0 if fewer than 2 rows). */
export function decisionMargin(page: ClusterPage): number {
  const scores = (page.classification ?? []).map((row) => row[1]).sort((a, b) => b - a);
  return scores.length >= 2 ? scores[0] - scores[1] : 0;
}

/**
 * Empirical confusability: for each non-self category appearing in members' top-5,
 * count how many members list it. Sorted by count descending.
 */
export function empiricalConfusability(
  pages: ClusterPage[],
  categoryName: string | undefined,
): { category: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const page of pages) {
    const seen = new Set<string>();
    for (const row of page.classification ?? []) {
      const category = row[0];
      if (category && category !== categoryName && !seen.has(category)) {
        seen.add(category);
        counts.set(category, (counts.get(category) ?? 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count);
}
